import { useCallback, useEffect, useState } from 'react';

import { authSession } from '@/auth/utils/authSession';
import {
  type SignInMethods,
  type SocialIdentity,
  type SocialProvider,
  connectSocialIdentity,
  disconnectSocialIdentity,
  fetchSignInMethods,
} from '@/auth/utils/socialSignIn';
import { useAppSelector } from '@/store';

export interface ConnectedAccountsState {
  loading: boolean;
  hasPassword: boolean;
  identities: SocialIdentity[];
  /** Rejects with the api's error; the list is read again either way. */
  connect: (provider: SocialProvider, idToken: string) => Promise<void>;
  /** Rejects with the api's error; the list is read again either way. */
  disconnect: (provider: SocialProvider) => Promise<void>;
  /** The last read of the list failed. */
  error?: unknown;
}

/** The viewer's connected Google and Microsoft accounts, and the two changes to them. */
export function useConnectedAccounts(): ConnectedAccountsState {
  const [methods, setMethods] = useState<SignInMethods>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>();
  // ENG-6300: a first password set on the same page lands in the viewer, not here.
  const viewerHasPassword = useAppSelector((state) => state.viewer.has_password === true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setMethods(await fetchSignInMethods(authSession.createClient()));
      setError(undefined);
    } catch (err) {
      setError(err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  // A refused change may still mean the list moved (another tab connected the
  // same provider), so it is read again on failure too.
  async function connect(provider: SocialProvider, idToken: string) {
    try {
      await connectSocialIdentity(authSession.createClient(), provider, idToken);
    } finally {
      await reload();
    }
  }

  async function disconnect(provider: SocialProvider) {
    try {
      await disconnectSocialIdentity(authSession.createClient(), provider);
    } finally {
      await reload();
    }
  }

  return {
    loading,
    hasPassword: (methods?.hasPassword ?? false) || viewerHasPassword,
    identities: methods?.identities ?? [],
    connect,
    disconnect,
    error,
  };
}
